import React from 'react';
import { FiX, FiCheck } from 'react-icons/fi';
import {
  MOBILE_FEED_SECTIONS,
  isFeedSectionEnabled,
  countEnabledSections,
} from '../config/mobileFeedSections';

/**
 * Picker for what the mobile Brief tab shows.
 *
 * Everything is an inline style on purpose. `src/tailwind-output.css` is a prebuilt
 * file with utilities simply missing from it (`sticky`, `top-0`, `z-10`, `text-gray-500`
 * …), and Bootstrap 5.3.3 loads from a CDN in public/index.html with `!important`
 * spacing utilities that collide by name with Tailwind's — both fail silently, so a
 * sheet built out of utility classes would be a coin flip. The surrounding mobile
 * shell in App.js is written the same way.
 */
const MobileFeedSettings = ({ open, sections, onToggle, onSetAll, onClose }) => {
  if (!open) {
    return null;
  }

  const enabledCount = countEnabledSections(sections);
  const total = MOBILE_FEED_SECTIONS.length;
  const allOn = enabledCount === total;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        background: 'rgba(15, 23, 42, 0.45)',
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        style={{
          background: '#fff',
          borderTopLeftRadius: '1.25rem',
          borderTopRightRadius: '1.25rem',
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -12px 32px rgba(15, 23, 42, 0.25)',
        }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Brief settings"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.1rem 0.75rem',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
              Customize your brief
            </span>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              {enabledCount} of {total} sections showing
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close feed settings"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '9999px',
              border: 'none',
              background: '#f1f5f9',
              color: '#475569',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            padding: '0.7rem 1.1rem',
            borderBottom: '1px solid #f1f5f9',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => onSetAll(true)}
            disabled={allOn}
            style={{
              flex: 1,
              padding: '0.5rem 0',
              borderRadius: '0.6rem',
              border: '1px solid #cbd5e1',
              background: allOn ? '#f8fafc' : '#fff',
              color: allOn ? '#94a3b8' : '#334155',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: allOn ? 'default' : 'pointer',
            }}
          >
            Show all
          </button>
          <button
            type="button"
            onClick={() => onSetAll(false)}
            disabled={enabledCount === 0}
            style={{
              flex: 1,
              padding: '0.5rem 0',
              borderRadius: '0.6rem',
              border: '1px solid #cbd5e1',
              background: enabledCount === 0 ? '#f8fafc' : '#fff',
              color: enabledCount === 0 ? '#94a3b8' : '#334155',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: enabledCount === 0 ? 'default' : 'pointer',
            }}
          >
            Hide all
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0.4rem 0.6rem 1.1rem', WebkitOverflowScrolling: 'touch' }}>
          {MOBILE_FEED_SECTIONS.map((section) => {
            const enabled = isFeedSectionEnabled(sections, section.key);
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => onToggle(section.key)}
                role="switch"
                aria-checked={enabled}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.7rem 0.6rem',
                  borderRadius: '0.75rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: '0.4rem',
                    flexShrink: 0,
                    border: enabled ? '1px solid #2563eb' : '1px solid #cbd5e1',
                    background: enabled ? '#2563eb' : '#fff',
                    color: '#fff',
                  }}
                >
                  {enabled && <FiCheck size={14} />}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: '0.88rem',
                      fontWeight: 600,
                      color: enabled ? '#0f172a' : '#64748b',
                    }}
                  >
                    {section.label}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.35 }}>
                    {section.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MobileFeedSettings;
